import { FunctionComponent, useRef, useEffect } from 'react'
import { SelectableGroup } from 'react-selectable-fast'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import advancedFormat from 'dayjs/plugin/advancedFormat'
import timezone from 'dayjs/plugin/timezone'
import SelectableQuota from './selectable-quotas'
import { FULL_DATE_FORMAT, getStartAndEndDayOfWeek } from '../../../../utils/dates'

dayjs.extend(utc)
dayjs.extend(advancedFormat)
dayjs.extend(timezone)

const INIT = 0
const NUM_OF_HOURS = 24

const getDefaultDate = (date: dayjs.Dayjs, timezone: string) => date.tz(timezone).startOf('week')

const getDayAtZeroHours = (date) => {
  return dayjs(date).set('hour', 0)
}

const getWeekDays = (timezone: string, date?: dayjs.Dayjs, daysOfDiff = 7) => {
  date = date ?? getDefaultDate(dayjs(), timezone)
  const utcDate = getDayAtZeroHours(date)
  const today = dayjs().format('DDD')
  const days = []
  for (let ix = 0; ix < daysOfDiff; ix++) {
    const day = utcDate.add(ix, 'days')
    const dayName = day.format('ddd')
    days.push({
      day: day.format('D'),
      dayName,
      date: day.format('YYYY-MM-DD'),
      today: String(today) === day.format('DDD'),
      active: parseInt(day.format('x')) >= parseInt(getDayAtZeroHours(dayjs()).format('x'))
    })
  }
  return days
}

const getHours = () => {
  const currentHour = new Date().getHours()
  let hours = []
  for (let ix = 0; ix < NUM_OF_HOURS; ix++) {
    const hour = INIT + ix
    hours.push({
      text: `${hour >= 10 ? (hour === 24 ? '00' : hour) : '0' + hour}:00`,
      value: hour === 24 ? 0 : hour,
      active: hour >= currentHour
    })
  }
  return hours
}

const getQuotasByTimeZone = (fleets: any, timezone: string) => {
  let formattedQuotas: any = {}
  fleets.forEach((fleet: any) => {
    const { quotas, value, label, color } = fleet
    quotas.forEach((quota: any) => {
      const { from } = quota
      const dateWithTz = dayjs(from).tz(timezone)
      const day = dateWithTz.date()
      if (!formattedQuotas[day]) {
        formattedQuotas[day] = {}
      }
      formattedQuotas[day][dateWithTz.hour()] = {
        quota,
        fleet: { id: value, name: label, color }
      }
    })
  })
  return formattedQuotas
}

export declare type Mode = 'view' | 'edit'

export type Quota = {
  id?: string
  from?: string
  to?: string
}

export type Fleet = {
  id?: string
  name?: string
  color?: string
  quotas?: Quota[]
}

export type FleetQuota = {
  fleet: Fleet
  quota: Quota
}

export type SelectedItem = {
  fleetQuota?: FleetQuota
  isActiveQuota?: boolean
  isSelected?: boolean
  mode?: Mode
  hour?: number
  date?: string
}

export type Bound = {
  top?: number
  left?: number
}

export type ResourceQuotasCalendarProps = {
  weekday?: dayjs.Dayjs
  timezone?: string
  fleets?: Fleet[]
  mode?: Mode
  onFinishSelection: (selectedItems: SelectedItem) => SelectedItem
  children?: JSX.Element
  onClick: () => void
  copy?: (text: string) => string
  refSelectableGroup?: any
  className?: string
}

const scrollToRef = (refCalendar: any, refToSix: any) => {
  refCalendar.current.scrollTo({
    top: refToSix.current.offsetTop,
    behavior: 'smooth'
  })
}

export const ResourceQuotasCalendar: FunctionComponent<ResourceQuotasCalendarProps> = ({
  weekday = dayjs(),
  timezone = 'America/Bogota',
  fleets = [],
  mode = 'edit',
  onFinishSelection,
  children,
  onClick = () => {},
  copy = (text: string) => text,
  refSelectableGroup = null,
  className = ''
}: ResourceQuotasCalendarProps): JSX.Element => {
  const refToSix = useRef(null)
  const refCalendar = useRef(null)
  const useMountEffect = (fun: () => void) => useEffect(fun, [mode, weekday])
  useMountEffect(() => scrollToRef(refCalendar, refToSix))

  const { from } = getStartAndEndDayOfWeek(weekday.format(FULL_DATE_FORMAT))
  const startDay = dayjs(from)

  const days = getWeekDays(timezone, startDay)
  const hours = getHours()
  const quotas = getQuotasByTimeZone(fleets, timezone)
  return (
    <div data-testid='gungnir-rqc' className={`gungnir-rqc ${className}`} onClick={onClick}>
      <div className='gungnir-rqc-rows'>
        <div className='gungnir-rqc-days'>
          <div>
            <span className='gungnir-rqc-day-name'>{copy('hour')}</span>
          </div>
        </div>
        {days.map(({ day, dayName, today }, ix) => {
          return (
            <div className='gungnir-rqc-days' key={ix}>
              <div className={`${today ? 'today' : ''}`}>
                <span className='gungnir-rqc-day-name'>{`${copy(dayName)} ${day}`}</span>
              </div>
            </div>
          )
        })}
      </div>
      <div className='gungnir-rqc-quotas-content' ref={refCalendar}>
        <SelectableGroup
          ref={refSelectableGroup}
          className='selectable-group'
          enableDeselect
          mixedDeselect
          deselectOnEsc
          allowClickWithoutSelected
          onSelectionFinish={(items: any) =>
            onFinishSelection(
              items
                .map(({ props, bounds }: { props: SelectedItem; bounds: Bound[] }) => {
                  return { props, bounds }
                })
                .filter(
                  ({ props: { isActiveQuota } }: { props: { isActiveQuota: boolean } }) =>
                    isActiveQuota && mode === 'edit'
                )
            )
          }
        >
          {hours.map(({ text, value, active }, idx) => (
            <div className='gungnir-rqc-rows' key={idx} ref={(value === 5 && refToSix) || null}>
              <div className='gungnir-rqc-hours'>
                <div className='gungnir-rqc-hour'>
                  <span className='gungnir-rqc-hour-name'>{text}</span>
                </div>
              </div>
              {days.map(({ day, today, date, active: isActiveDay }, ix) => {
                const isActiveQuota = today ? active : isActiveDay
                const fleetQuota = quotas[day] ? quotas[day][value] : null
                return (
                  <SelectableQuota
                    key={ix}
                    hour={value}
                    date={date}
                    isActiveQuota={isActiveQuota}
                    fleetQuota={fleetQuota}
                    mode={mode}
                  />
                )
              })}
            </div>
          ))}
        </SelectableGroup>
      </div>
      {children}
    </div>
  )
}
